import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { parseHash, startOfToday, viewToHash, type View } from './App.js';

/**
 * The URL-hash routing cluster (audit ARCH-006): the two effects that keep `window.location.hash` and
 * the active `view`/`anchorDate` in sync, extracted verbatim from App.tsx so linkable URLs, browser
 * Back/Forward, and manual hash edits behave byte-for-byte identically.
 *
 * - WRITE: mirror the active surface into the hash. A genuine in-app navigation PUSHES a history entry
 *   (Back/Forward moves between surfaces); the first write (hydration) REPLACES so we don't leave a
 *   phantom entry above the loaded page. `lastHashRef` records what we wrote so the read listener
 *   ignores our own updates (and the early-return guard keeps a Back-triggered setView from immediately
 *   re-pushing the same hash — no loop). Skipped while an OAuth consent request is in flight so its
 *   params aren't normalized away.
 * - READ: `popstate` (Back/Forward) or `hashchange` (manual edit) re-navigates. A bare `#/journal`
 *   re-anchors to the live "today"; a dated hash pins that day.
 *
 * The pure `parseHash`/`viewToHash`/`startOfToday` helpers and the `View`/`Route` types stay in App.tsx
 * (exercised directly by App.helpers.test.ts) and are imported here.
 */
export function useHashRoute(opts: {
  view: View;
  anchorDate: Date;
  setView: Dispatch<SetStateAction<View>>;
  jumpToDate: (d: Date) => void;
  /** Truthy while an OAuth consent request is in flight — the write effect skips so its params survive. */
  consentRequest: unknown;
}): void {
  const { view, anchorDate, setView, jumpToDate, consentRequest } = opts;

  // The very first write (hydration) REPLACES so we don't leave a phantom entry above the page you
  // loaded. `lastHashRef` records what we wrote so the read listener ignores our own updates.
  const lastHashRef = useRef<string>('');
  const hashHydratedRef = useRef(false);
  useEffect(() => {
    // While an OAuth consent request is in flight the URL carries its params — don't normalize it away.
    if (consentRequest) return;
    const hash = viewToHash(view, anchorDate);
    const first = !hashHydratedRef.current;
    hashHydratedRef.current = true;
    if (window.location.hash === hash) {
      lastHashRef.current = hash;
      return;
    }
    lastHashRef.current = hash;
    if (first) window.history.replaceState(null, '', hash);
    else window.history.pushState(null, '', hash);
  }, [view, anchorDate, consentRequest]);

  // URL-hash sync (read): Back/Forward (popstate) or a manual hash edit (hashchange) re-navigates.
  // Skip hashes we just wrote ourselves.
  useEffect(() => {
    const apply = (): void => {
      if (window.location.hash === lastHashRef.current) return;
      const route = parseHash(window.location.hash);
      if (!route) return;
      if (route.kind === 'journals') {
        // A dated hash pins that day; a bare `#/journal` is the live "today" view — re-anchor so
        // Back/forward into it resumes tracking rather than leaving a stale overnight anchor.
        jumpToDate(route.date ?? startOfToday());
        setView({ kind: 'journals' });
      } else if (route.kind === 'doc') setView({ kind: 'doc', noteId: route.noteId });
      else if (route.kind === 'library') setView({ kind: 'library' });
      else if (route.kind === 'reddit') setView({ kind: 'reddit' });
      else if (route.kind === 'integrations') setView({ kind: 'integrations' });
      else if (route.kind === 'integration') setView({ kind: 'integration', id: route.id });
      else if (route.kind === 'utilities') setView({ kind: 'utilities' });
      else if (route.kind === 'utility') setView({ kind: 'utility', id: route.id });
      else if (route.kind === 'subscription') setView({ kind: 'subscription' });
      else if (route.kind === 'finances') setView({ kind: 'finances' });
      else if (route.kind === 'fitness') setView({ kind: 'fitness' });
      else if (route.kind === 'kudos') setView({ kind: 'kudos' });
      else if (route.kind === 'shopping') setView({ kind: 'shopping' });
      else if (route.kind === 'bills') setView({ kind: 'bills', billId: route.billId });
      else if (route.kind === 'decisions') setView({ kind: 'decisions', openId: route.openId });
      else if (route.kind === 'ai-coaching') setView({ kind: 'ai-coaching', module: route.module });
      else if (route.kind === 'initiatives') setView({ kind: 'initiatives', openName: route.openName });
      else if (route.kind === 'contact') setView({ kind: 'contact', contactId: route.contactId });
      else setView({ kind: 'contacts' });
    };
    window.addEventListener('popstate', apply);
    window.addEventListener('hashchange', apply);
    return () => {
      window.removeEventListener('popstate', apply);
      window.removeEventListener('hashchange', apply);
    };
  }, [jumpToDate, setView]);
}
